import { ReactNode } from 'react'
import { cn } from '../../lib/utils'

// The canonical "page whose main content is a table". A white panel that fills
// its parent's height (give it `className="min-h-0 flex-1"` inside a flex
// column) with a slim header-toolbar, a scrolling body (the DataTable, whose
// column header sticks), and an optional pinned footer (e.g. Pagination).
//
// Use this for a page's MAIN table instead of wrapping a DataTable in a
// SectionCard: grey stays a separator (never a surface under the table) and a
// short table still fills the height — no grey gap below.
//
// EXCEPTION — a SMALL table inside a dashboard/widget grid (one card among
// several in a row, sized by its content rather than the page) is just
// `<SectionCard noBodyPadding><DataTable/></SectionCard>`. TablePanel is for the
// full-height page table; SectionCard+DataTable is for an in-widget table.
export interface TablePanelProps {
  icon?: ReactNode
  title: ReactNode
  count?: number | string
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export function TablePanel({ icon, title, count, subtitle, actions, children, footer, className }: TablePanelProps) {
  return (
    <section className={cn('flex min-h-0 flex-col overflow-hidden rounded-xl bg-card ring-1 ring-border', className)}>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {count != null && <span className="text-[11px] tabular-nums text-muted-foreground">· {count}</span>}
        {subtitle && <span className="text-[11px] text-muted-foreground">· {subtitle}</span>}
        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      {footer && <div className="shrink-0 border-t border-border">{footer}</div>}
    </section>
  )
}
